import {
    FeedbackRequestWithDetails,
    FeedbackWithDetails,
} from '@/common/types/databaseTypes';
import { useAuthStore } from '@/stores/authStore';
import { User } from '@tickitacka/shared';
import { create } from 'zustand';

export type FeedbackModalMode =
    | 'send' // 피드백 생성
    | 'request' // 피드백 요청 생성
    | 'respond' // 요청에 대한 피드백 생성
    | 'edit-feedback' // 피드백 수정
    | 'edit-request'; // 피드백 요청 수정

interface FeedbackPageModalState {
    // 모달 제어 상태
    isOpen: boolean;
    mode: FeedbackModalMode;
    data?: FeedbackWithDetails | FeedbackRequestWithDetails | null;

    // 모달 내부 폼 상태
    feedbackContent: string;
    selectedUser: Partial<User> | null;
    requestUser: Partial<User> | null;
    positiveCoreValueIds: string[];
    negativeCoreValueIds: string[];
    selectedTodoMapList: { id: string; title: string }[];

    requestUpdated: boolean;

    // 모달 제어 액션
    openModal: (
        mode: FeedbackModalMode,
        options?: {
            data?: FeedbackWithDetails | FeedbackRequestWithDetails | null;
        }
    ) => void;
    closeModal: () => void;

    // 폼 상태 액션
    setFeedbackContent: (content: string) => void;
    setSelectedUser: (user: Partial<User> | null) => void;
    setRequestUser: (user: Partial<User> | null) => void;
    setPositiveCoreValueIds: (ids: string[]) => void;
    setNegativeCoreValueIds: (ids: string[]) => void;
    setSelectedTodoMapList: (ids: { id: string; title: string }[]) => void;
    resetFormState: () => void;

    setRequestUpdated: (updated: boolean) => void;
    updateRequestData: (newData: FeedbackRequestWithDetails | null) => void;
}

const useFeedbackModalStore = create<FeedbackPageModalState>(set => ({
    // 모달 제어 상태
    isOpen: false,
    mode: 'send',
    data: null,

    // 모달 내부 폼 상태
    feedbackContent: '',
    selectedUser: null,
    requestUser: null,
    positiveCoreValueIds: [],
    negativeCoreValueIds: [],
    selectedTodoMapList: [],

    requestUpdated: false,

    // 모달 제어 액션
    openModal: (
        mode: FeedbackModalMode,
        options?: {
            data?: FeedbackWithDetails | FeedbackRequestWithDetails | null;
        }
    ) => {
        const { user } = useAuthStore.getState();
        const defaultData = options?.data;

        // 편집 모드일 때 기본 피드백 내용 가져오기
        let defaultContent = '';
        if (mode === 'edit-feedback' && defaultData) {
            defaultContent = (defaultData as FeedbackWithDetails).content || '';
        } else if (mode === 'edit-request' && defaultData) {
            defaultContent =
                (defaultData as FeedbackRequestWithDetails).content || '';
        }

        // 편집 모드일 때 기본 선택된 사용자 결정
        let defaultSelectedUser: Partial<User> | null = null;
        if (mode === 'edit-feedback' && defaultData) {
            defaultSelectedUser =
                (defaultData as FeedbackWithDetails).receiver || null;
        } else if (mode === 'edit-request' && defaultData) {
            defaultSelectedUser =
                (defaultData as FeedbackRequestWithDetails).receiver || null;
        }

        // 편집 모드일 때 핵심가치 ID 추출
        const positiveIds: string[] = [];
        const negativeIds: string[] = [];

        if (mode === 'edit-feedback' && defaultData) {
            const feedbackData = defaultData as FeedbackWithDetails;
            feedbackData.feedback_core_values?.forEach(cv => {
                if (cv.is_positive) {
                    positiveIds.push(cv.core_value.id);
                } else {
                    negativeIds.push(cv.core_value.id);
                }
            });
        }

        // 선택된 투두 ID 추출
        const todoIds: { id: string; title: string }[] = [];
        if (mode === 'edit-feedback' && defaultData) {
            (defaultData as FeedbackWithDetails).feedback_todos?.forEach(ft => {
                if (ft.todo?.id)
                    todoIds.push({ id: ft.todo.id, title: ft.todo.title });
            });
        } else if (
            (mode === 'edit-request' || mode === 'respond') &&
            defaultData
        ) {
            (
                defaultData as FeedbackRequestWithDetails
            ).feedback_request_todos?.forEach(ft => {
                if (ft.todo?.id)
                    todoIds.push({ id: ft.todo.id, title: ft.todo.title });
            });
        }

        set({
            isOpen: true,
            mode,
            data: defaultData,
            feedbackContent: defaultContent,
            selectedUser: defaultSelectedUser,
            requestUser:
                mode === 'request' || mode === 'edit-request' ? user : null,
            positiveCoreValueIds: positiveIds,
            negativeCoreValueIds: negativeIds,
            selectedTodoMapList: todoIds,
            requestUpdated: false,
        });
    },
    closeModal: () =>
        set({
            isOpen: false,
            // 모달이 완전히 닫힐 때만 폼 상태 초기화
            feedbackContent: '',
            selectedUser: null,
            requestUser: null,
            positiveCoreValueIds: [],
            negativeCoreValueIds: [],
            selectedTodoMapList: [],
            requestUpdated: false,
        }),

    // 폼 상태 관련 액션
    setFeedbackContent: content => set({ feedbackContent: content }),
    setSelectedUser: user => set({ selectedUser: user }),
    setRequestUser: user => set({ requestUser: user }),
    setPositiveCoreValueIds: ids => set({ positiveCoreValueIds: ids }),
    setNegativeCoreValueIds: ids => set({ negativeCoreValueIds: ids }),
    setSelectedTodoMapList: (map: { id: string; title: string }[]) =>
        set({ selectedTodoMapList: map }),

    resetFormState: () =>
        set({
            feedbackContent: '',
            selectedUser: null,
            requestUser: null,
            positiveCoreValueIds: [],
            negativeCoreValueIds: [],
            selectedTodoMapList: [],
            requestUpdated: false,
        }),

    setRequestUpdated: updated => set({ requestUpdated: updated }),
    updateRequestData: (newData: FeedbackRequestWithDetails | null) =>
        set(state => ({
            // 사용자가 작성한 내용은 보존
            ...state,
            data: newData,
            // 요청된 todo 목록은 업데이트될 수 있으므로 새 데이터를 반영
            selectedTodoMapList:
                newData?.feedback_request_todos?.map(ft => ({
                    id: ft.todo?.id || '',
                    title: ft.todo?.title || '',
                })) || state.selectedTodoMapList,
            requestUpdated: false,
        })),
}));

export default useFeedbackModalStore;
